import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
};

const paperStyles = {
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  maxHeight: '90dvh',
  pb: 'env(safe-area-inset-bottom)',
} as const;

const handleStyles = {
  width: 36,
  height: 4,
  bgcolor: 'action.disabled',
  borderRadius: 100,
  mx: 'auto',
  mt: 1,
} as const;

const headerStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2,
  py: 1,
  borderBottom: 1,
  borderColor: 'divider',
  position: 'sticky',
  top: 0,
  bgcolor: 'background.paper',
  zIndex: 1,
} as const;

const contentStyles = { px: 2, py: 2, overflowY: 'auto' } as const;

export const BottomSheet = ({ open, onClose, title, children }: BottomSheetProps) => {
  const { t } = useTranslation();
  return (
    // Modal stacking layers:
    //   1300: default MUI Dialog (e.g., MobileJobDetailSheet)
    //   1301: BottomSheet (mobile editor sheets that open over the detail Dialog)
    //   1302: UnsavedChangesDialog (sits above any BottomSheet for discard-confirm)
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: paperStyles }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <Box sx={handleStyles} />
      {title && (
        <Box sx={headerStyles}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label={t('common.close', { defaultValue: 'Close' })}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <Box sx={contentStyles}>{children}</Box>
    </Drawer>
  );
};
